import { ReactNode, useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { api } from "@/api/client";
import { Avatar } from "@/components/Avatar";
import { ManualPlacementModal } from "@/components/ManualPlacementModal";
import { PageHeader } from "@/components/PageHeader";
import { PlatformBadge } from "@/components/PlatformLogo";
import { Skeleton } from "@/components/Skeleton";
import {
  FilterChip,
  FilterChips,
  FilterSection,
  InlineMultiSelect,
  InlineRange,
  PresetChips,
  SegToggle,
  SingleSelectDropdown,
  ToggleRow,
  type ActiveChip,
  type FacetOption,
  type RangePreset,
} from "@/components/FilterControls";
import { FiltersDrawer } from "@/components/FiltersDrawer";
import {
  ActivePublicLinksPanel,
  ShareEditModal,
  ShareFromUrlsButton,
  ShareLinkModal,
  SharePrepareModal,
  useShareLinks,
} from "@/components/ShareLinks";
import { BUILDING_STATUS_OPTIONS, CONDITION_OPTIONS } from "@/components/MarketShared";
import type {
  OfferKind,
  Placement,
  PlacementDistrict,
  PlacementSort,
  PlacementStats,
  PlacementStreet,
  Platform,
  PlatformDeleteResult,
  TeamMember,
} from "@/types";

// ---------- Formatting helpers ----------

function formatPrice(price: string | null, currency: string | null) {
  if (!price) return "—";
  const n = Number(price);
  if (!Number.isFinite(n)) return price;
  const symbol = currency === "USD" ? "$" : currency === "GEL" ? "₾" : currency ? currency + " " : "";
  return `${symbol}${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

const PROPERTY_TYPE_LABEL: Record<string, string> = {
  apartment: "Apartment",
  house: "House",
  cottage: "Cottage",
  land: "Land",
  commercial: "Commercial",
};

const PROPERTY_TYPES: { id: string; label: string; icon: string }[] = [
  { id: "apartment", label: "Apartment", icon: "🏢" },
  { id: "house", label: "House", icon: "🏠" },
  { id: "cottage", label: "Cottage", icon: "🏡" },
  { id: "land", label: "Land", icon: "🌳" },
  { id: "commercial", label: "Commercial", icon: "🏬" },
];

/** Short label for a min/max range chip, e.g. "50k–100k", "3+ fl", "≤70 m²". */
function rangeLabel(
  base: string,
  min: string,
  max: string,
  fmt: (v: string) => string,
): string {
  if (min && max) return `${fmt(min)}–${fmt(max)} ${base}`;
  if (min) return `${fmt(min)}+ ${base}`;
  if (max) return `≤${fmt(max)} ${base}`;
  return base;
}

function compactNum(v: string): string {
  const n = Number(v);
  if (!Number.isFinite(n)) return v;
  return n >= 1000 ? `${n / 1000}k` : String(n);
}

function formatFloor(floor: number | null, total: number | null) {
  if (floor == null) return null;
  return total != null ? `${floor}/${total}` : String(floor);
}

// Relative time without pulling in a dep — "2h ago", "3d ago", or a date for
// anything past a week. Keeps the card footer compact while still showing
// the actual date on hover via the title attribute.
function relativeTime(iso: string): string {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return "";
  const diff = Date.now() - t;
  const min = Math.floor(diff / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  const d = Math.floor(hr / 24);
  if (d < 7) return `${d}d`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function formatExactDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function fullSizeImage(src: string): string {
  return src
    .replace(/_Thumb(\.[a-zA-Z]+)(\?|$)/, "$1$2")
    .replace(/_thumb(\.[a-zA-Z]+)(\?|$)/, "$1$2");
}

function formatOffer(kind: OfferKind | null, value: string | null, currency: string | null): string {
  if (!kind || value == null) return "";
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  if (kind === "percent") {
    return `${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
  }
  const sym = currency === "USD" ? "$" : currency === "GEL" ? "₾" : currency ? `${currency} ` : "";
  return `${sym}${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function specsLine(p: Placement): string {
  const parts: string[] = [];
  if (p.rooms != null) parts.push(`${p.rooms} rm`);
  if (p.area_sqm) parts.push(`${Number(p.area_sqm).toLocaleString(undefined, { maximumFractionDigits: 1 })} m²`);
  const floor = formatFloor(p.floor, p.total_floors);
  if (floor) parts.push(`fl ${floor}`);
  if (p.property_type) parts.push(PROPERTY_TYPE_LABEL[p.property_type] ?? p.property_type);
  return parts.join(" · ");
}

// ---------- Small inline editors ----------

/** The portals hand back a partially redacted number (``599352***``) for any
 *  listing the agency hasn't unlocked. It's not a contact — same convention as
 *  the market cards and the publish modal, and the "missing phone" counter on
 *  the backend counts these too. */
const isMaskedPhone = (v: string | null | undefined) => !!v && v.includes("*");

function PhoneEditor({
  placementIds,
  value,
  canEdit,
  onSaved,
}: {
  placementIds: string[];
  value: string | null;
  canEdit: boolean;
  onSaved: (next: string | null) => void;
}) {
  const masked = isMaskedPhone(value);
  const seed = masked ? "" : value || "";
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(seed);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    setBusy(true);
    setErr(null);
    try {
      const next = draft.trim() || null;
      await Promise.all(
        placementIds.map((id) =>
          api(`/api/placements/${id}`, { method: "PATCH", json: { owner_phone: next } }),
        ),
      );
      onSaved(next);
      setEditing(false);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  if (!canEdit) {
    return (
      <span className={`mono text-xs ${masked ? "text-amber-400/70" : "text-slate-300"}`}
        title={masked ? "Hidden by the portal — the real number is still missing" : undefined}>
        {value || <span className="text-slate-600 italic">no phone</span>}
      </span>
    );
  }

  if (editing) {
    return (
      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
        <input
          type="tel"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          disabled={busy}
          autoFocus
          onKeyDown={(e) => {
            if (e.key === "Enter") save();
            if (e.key === "Escape") { setDraft(seed); setEditing(false); }
          }}
          placeholder="+995 5xx …"
          className="input py-0.5 px-1.5 text-[11px] w-32 mono"
        />
        <button type="button" onClick={save} disabled={busy} title="Save"
          className="text-[11px] px-1.5 py-0.5 rounded bg-accent-500/20 text-accent-300 hover:bg-accent-500/30 disabled:opacity-50">✓</button>
        <button type="button" onClick={() => { setDraft(seed); setEditing(false); setErr(null); }}
          disabled={busy} title="Cancel"
          className="text-[11px] px-1.5 py-0.5 rounded bg-white/[0.04] text-slate-400 hover:bg-white/[0.08]">✕</button>
        {err && <span className="text-[10px] text-rose-400">{err}</span>}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); setEditing(true); }}
      className="group/phone inline-flex items-center gap-1 text-left text-slate-300 hover:text-accent-300"
      title={masked ? "Hidden by the portal — click to add the real number" : "Click to edit phone"}
    >
      {value ? (
        <span className={`mono text-xs ${masked ? "text-amber-400/70" : ""}`}>{value}</span>
      ) : (
        <span className="text-slate-500 italic text-xs">add phone…</span>
      )}
      <span className="opacity-0 group-hover/phone:opacity-60 text-[9px]">✎</span>
    </button>
  );
}

function NotesEditor({
  placementIds,
  value,
  canEdit,
  onSaved,
}: {
  placementIds: string[];
  value: string | null;
  canEdit: boolean;
  onSaved: (next: string | null) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    setBusy(true);
    setErr(null);
    try {
      const next = draft.trim() || null;
      // Patch every platform publication in the group so MyHome + SS rows
      // stay in lock-step (same pattern as PhoneEditor).
      await Promise.all(
        placementIds.map((id) =>
          api(`/api/placements/${id}`, { method: "PATCH", json: { notes: next } }),
        ),
      );
      onSaved(next);
      setEditing(false);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  if (editing) {
    return (
      <div className="flex flex-col gap-1 w-full" onClick={(e) => e.stopPropagation()}>
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          disabled={busy}
          autoFocus
          rows={3}
          maxLength={4000}
          placeholder="Owner availability, negotiation state…"
          onKeyDown={(e) => {
            if (e.key === "Escape") { setDraft(value || ""); setEditing(false); setErr(null); }
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) save();
          }}
          className="input py-1 px-1.5 text-[11px] w-full resize-y leading-snug"
        />
        <div className="flex items-center gap-1">
          <button type="button" onClick={save} disabled={busy}
            className="text-[11px] px-1.5 py-0.5 rounded bg-accent-500/20 text-accent-300 hover:bg-accent-500/30 disabled:opacity-50">
            {busy ? "…" : "✓ Save"}
          </button>
          <button type="button" onClick={() => { setDraft(value || ""); setEditing(false); setErr(null); }}
            disabled={busy}
            className="text-[11px] px-1.5 py-0.5 rounded bg-white/[0.04] text-slate-400 hover:bg-white/[0.08]">
            Cancel
          </button>
          {err && <span className="text-[10px] text-rose-400">{err}</span>}
        </div>
      </div>
    );
  }

  if (!canEdit) {
    if (!value) {
      return <span className="text-slate-600 italic text-xs">—</span>;
    }
    return (
      <span className="text-[12px] text-slate-300 line-clamp-2" title={value}>{value}</span>
    );
  }

  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); setEditing(true); }}
      className="group/notes inline-flex items-start gap-1 text-left text-slate-300 hover:text-accent-300 min-w-0"
      title="Click to edit notes"
    >
      {value ? (
        <span className="text-[12px] line-clamp-2 leading-snug">{value}</span>
      ) : (
        <span className="text-slate-500 italic text-xs">add notes…</span>
      )}
      <span className="opacity-0 group-hover/notes:opacity-60 text-[9px] flex-shrink-0">✎</span>
    </button>
  );
}

function OfferEditor({
  placement,
  canEdit,
  onSaved,
}: {
  placement: Placement;
  canEdit: boolean;
  onSaved: (next: { offer_kind: OfferKind | null; offer_value: string | null }) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [kind, setKind] = useState<OfferKind>(placement.offer_kind || "percent");
  const [value, setValue] = useState<string>(placement.offer_value ?? "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const groupIds = placement.platforms.length ? placement.platforms.map((p) => p.id) : [placement.id];

  function startEdit() {
    setKind(placement.offer_kind || "percent");
    setValue(placement.offer_value ?? "");
    setErr(null);
    setEditing(true);
  }

  async function save() {
    setErr(null);
    const trimmed = value.trim();
    if (!trimmed) { return clear(); }
    const n = Number(trimmed);
    if (!Number.isFinite(n) || n < 0) { setErr("Bad number"); return; }
    if (kind === "percent" && n > 100) { setErr("0–100"); return; }
    setBusy(true);
    try {
      await Promise.all(
        groupIds.map((id) =>
          api(`/api/placements/${id}`, { method: "PATCH", json: { offer_kind: kind, offer_value: n } }),
        ),
      );
      onSaved({ offer_kind: kind, offer_value: String(n) });
      setEditing(false);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  async function clear() {
    setBusy(true);
    setErr(null);
    try {
      await Promise.all(
        groupIds.map((id) =>
          api(`/api/placements/${id}`, { method: "PATCH", json: { offer_kind: null } }),
        ),
      );
      onSaved({ offer_kind: null, offer_value: null });
      setEditing(false);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  const formatted = formatOffer(placement.offer_kind, placement.offer_value, placement.currency);

  if (editing) {
    return (
      <div className="flex items-center gap-1 flex-wrap" onClick={(e) => e.stopPropagation()}>
        <select value={kind} onChange={(e) => setKind(e.target.value as OfferKind)} disabled={busy}
          className="input py-0.5 px-1 text-[11px]" title="Percent or fixed amount">
          <option value="percent">%</option>
          <option value="fixed">
            {placement.currency === "USD" ? "$" : placement.currency === "GEL" ? "₾" : placement.currency || "amt"}
          </option>
        </select>
        <input type="number" step="0.01" min="0" value={value} onChange={(e) => setValue(e.target.value)}
          disabled={busy} autoFocus
          onKeyDown={(e) => {
            if (e.key === "Enter") save();
            if (e.key === "Escape") { setEditing(false); setErr(null); }
          }}
          placeholder={kind === "percent" ? "2.5" : "500"}
          className="input py-0.5 px-1.5 text-[11px] w-16 tabular-nums" />
        <button type="button" onClick={save} disabled={busy} title="Save"
          className="text-[11px] px-1.5 py-0.5 rounded bg-accent-500/20 text-accent-300 hover:bg-accent-500/30 disabled:opacity-50">✓</button>
        <button type="button" onClick={() => { setEditing(false); setErr(null); }} disabled={busy} title="Cancel"
          className="text-[11px] px-1.5 py-0.5 rounded bg-white/[0.04] text-slate-400 hover:bg-white/[0.08]">✕</button>
        {placement.offer_kind && (
          <button type="button" onClick={clear} disabled={busy} title="Remove"
            className="text-[11px] px-1.5 py-0.5 rounded text-slate-500 hover:text-rose-400 hover:bg-rose-500/10">🗑</button>
        )}
        {err && <span className="text-[10px] text-rose-400">{err}</span>}
      </div>
    );
  }

  if (!canEdit) {
    if (!formatted) return null;
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-300 text-[11px] font-semibold tabular-nums">
        {formatted}
      </span>
    );
  }

  return (
    <button type="button" onClick={(e) => { e.stopPropagation(); startEdit(); }}
      title="Click to edit commission"
      className={`group/offer inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-semibold tabular-nums transition ${
        formatted
          ? "bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20"
          : "bg-white/[0.03] text-slate-500 italic hover:bg-white/[0.06] hover:text-slate-300"
      }`}>
      {formatted || "set offer…"}
      <span className="opacity-0 group-hover/offer:opacity-60 text-[9px] not-italic">✎</span>
    </button>
  );
}

// ---------- Lightbox ----------

function Lightbox({ src, alt, onClose }: { src: string; alt: string; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-in fade-in duration-150"
      onClick={onClose} role="dialog" aria-modal="true">
      <button type="button" onClick={onClose} aria-label="Close"
        className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center text-xl transition">✕</button>
      <img src={fullSizeImage(src)} alt={alt} onClick={(e) => e.stopPropagation()}
        onError={(e) => { if (e.currentTarget.src !== src) e.currentTarget.src = src; }}
        className="max-w-[min(1200px,95vw)] max-h-[90vh] rounded-lg shadow-2xl object-contain" />
    </div>
  );
}

// ---------- Stats strip ----------

interface QuickFilter {
  myListings: boolean;
  missingPhone: boolean;
  missingOffer: boolean;
  manualOnly: boolean;
  thisWeek: boolean;
}

const QUICK_LABELS: Record<keyof QuickFilter, string> = {
  myListings: "My listings",
  missingPhone: "Missing phone",
  missingOffer: "Missing offer",
  manualOnly: "Manual only",
  thisWeek: "This week",
};

/** One cell of the metrics strip. Clickable cells double as quick filters —
 *  an active one lights up in accent instead of growing a ring. */
function MetricCell({
  label,
  value,
  sub,
  active,
  onClick,
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  active?: boolean;
  onClick?: () => void;
}) {
  const cls = `px-3 py-2.5 text-center transition ${
    active ? "bg-accent-500/[0.07]" : ""
  } ${onClick ? "hover:bg-white/[0.03] cursor-pointer" : "cursor-default"}`;
  const content = (
    <>
      <div
        className={`text-lg font-semibold tabular-nums leading-none ${
          active ? "text-accent-200" : "text-slate-100"
        }`}
      >
        {value}
      </div>
      <div className="text-[10px] uppercase tracking-wide text-slate-500 mt-1">{label}</div>
      {sub && <div className="text-[10px] text-slate-500 mt-0.5 truncate">{sub}</div>}
    </>
  );
  return onClick ? (
    <button type="button" onClick={onClick} className={`${cls} w-full`}>{content}</button>
  ) : (
    <div className={cls}>{content}</div>
  );
}

/** Slim metrics strip above the toolbar — same numbers (and the same
 *  click-to-filter behaviour) the old four big tiles had, in one row. */
function StatsStrip({
  stats,
  quick,
  toggle,
}: {
  stats: PlacementStats | null;
  quick: QuickFilter;
  toggle: (k: keyof QuickFilter) => void;
}) {
  const shell =
    "mb-4 rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden grid grid-cols-2 md:grid-cols-4 divide-x divide-white/5";

  if (!stats) {
    return (
      <div className={shell}>
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="px-3 py-2.5 flex flex-col items-center gap-1.5">
            <Skeleton className="h-5 w-14" />
            <Skeleton className="h-2.5 w-16" />
          </div>
        ))}
      </div>
    );
  }

  const delta = stats.this_week_delta;
  const deltaNode = delta === 0 ? (
    <span className="text-slate-500">±0 vs. prev wk</span>
  ) : delta > 0 ? (
    <span className="text-emerald-400">▲ +{delta} vs. prev wk</span>
  ) : (
    <span className="text-rose-400">▼ {delta} vs. prev wk</span>
  );

  const missing = stats.missing_phone + stats.missing_offer;

  return (
    <div className={shell}>
      <MetricCell
        label="total"
        value={stats.total.toLocaleString()}
        sub={`MH ${stats.myhome} · SS ${stats.ss}`}
      />
      <MetricCell
        label="this week"
        value={stats.this_week.toLocaleString()}
        sub={deltaNode}
        active={quick.thisWeek}
        onClick={() => toggle("thisWeek")}
      />
      <MetricCell
        label="missing data"
        value={
          <span className={missing > 0 ? "text-amber-300" : undefined}>
            {missing.toLocaleString()}
          </span>
        }
        active={quick.missingPhone || quick.missingOffer}
        sub={
          <>
            <button
              type="button"
              onClick={() => toggle("missingPhone")}
              className={`underline-offset-2 ${quick.missingPhone ? "text-accent-300 underline" : "hover:underline"}`}
            >
              {stats.missing_phone} phone
            </button>
            <span className="text-slate-700"> · </span>
            <button
              type="button"
              onClick={() => toggle("missingOffer")}
              className={`underline-offset-2 ${quick.missingOffer ? "text-accent-300 underline" : "hover:underline"}`}
            >
              {stats.missing_offer} offer
            </button>
          </>
        }
      />
      <MetricCell
        label="manual"
        value={stats.manual.toLocaleString()}
        sub="logged by hand"
        active={quick.manualOnly}
        onClick={() => toggle("manualOnly")}
      />
    </div>
  );
}

// ---------- Card ----------

function PlatformChips({ p }: { p: Placement }) {
  const pubs = p.platforms.length ? p.platforms : [{ id: p.id, platform: p.platform, published_url: p.published_url, external_id: p.external_id }];
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {pubs.map((pp) => {
        const deleted = Boolean((pp as { deleted_at?: string | null }).deleted_at);
        return (
          <a key={pp.id} href={pp.published_url} target="_blank" rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className={`inline-flex ${deleted ? "opacity-40 grayscale" : ""}`}
            title={
              deleted
                ? `Deleted on ${pp.platform === "myhome" ? "MyHome.ge" : "SS.ge"}`
                : `Open on ${pp.platform === "myhome" ? "MyHome.ge" : "SS.ge"}`
            }>
            <PlatformBadge platform={pp.platform as Platform} />
          </a>
        );
      })}
    </div>
  );
}

// Source-listing description — collapsed to a few lines by default with a
// Show more / Show less toggle. Only rendered when the row carries one
// (manual entries and pre-feature rows have none).
function DescriptionBlock({ text }: { text: string }) {
  const [expanded, setExpanded] = useState(false);
  // Only worth a toggle when there's enough text to clamp. The threshold is a
  // rough proxy for "longer than ~3 lines"; short blurbs just render in full.
  const clampable = text.length > 140;
  return (
    <div className="text-[12px] text-slate-400 leading-snug">
      <div
        className={
          expanded || !clampable ? "whitespace-pre-line" : "whitespace-pre-line line-clamp-3"
        }
      >
        {text}
      </div>
      {clampable && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
          className="mt-0.5 text-[11px] font-medium text-accent-400 hover:text-accent-300"
        >
          {expanded ? "Show less" : "Show more"}
        </button>
      )}
    </div>
  );
}

function PlacementCard({
  p,
  selected,
  selectable,
  canEditOffer,
  canEditPhone,
  canPortalDelete,
  onPortalDelete,
  onToggleSelect,
  onOpenPhoto,
  onUpdatePhone,
  onUpdateOffer,
  onUpdateNotes,
}: {
  p: Placement;
  selected: boolean;
  selectable: boolean;
  canEditOffer: boolean;
  canEditPhone: boolean;
  canPortalDelete: boolean;
  onPortalDelete: () => Promise<PlatformDeleteResult[]>;
  onToggleSelect: () => void;
  onOpenPhoto: () => void;
  onUpdatePhone: (next: string | null) => void;
  onUpdateOffer: (next: { offer_kind: OfferKind | null; offer_value: string | null }) => void;
  onUpdateNotes: (next: string | null) => void;
}) {
  const groupIds = p.platforms.length ? p.platforms.map((x) => x.id) : [p.id];
  const formattedOffer = formatOffer(p.offer_kind, p.offer_value, p.currency);
  const shortTitle = p.title?.trim() || (p.external_id ? `Listing ${p.external_id}` : "Untitled listing");
  const primaryUrl = p.published_url;

  const platforms = p.platforms.length ? p.platforms : [];
  // Always at least one entry to render an open-button for — fall back to
  // the primary row when the group array is empty.
  const pubs = p.platforms.length
    ? p.platforms
    : [{
        id: p.id,
        platform: p.platform,
        published_url: p.published_url,
        external_id: p.external_id,
        deleted_at: null,
        portal_delete_status: null,
      }];
  const deletablePlatforms = platforms.filter((x) => x.external_id);
  const allDeleted =
    deletablePlatforms.length > 0 && deletablePlatforms.every((x) => x.deleted_at);
  const hasExternal = Boolean(p.external_id) || deletablePlatforms.length > 0;

  const [confirming, setConfirming] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [delErr, setDelErr] = useState<string | null>(null);

  async function runDelete() {
    setDeleting(true);
    setDelErr(null);
    try {
      const res = await onPortalDelete();
      const failed = res.filter((r) => !r.ok);
      if (failed.length) {
        setDelErr(
          failed
            .map((r) => `${r.platform}: ${r.error || "failed"}`)
            .join(" · "),
        );
      } else {
        setConfirming(false);
        setConfirmText("");
      }
    } catch (e) {
      setDelErr(e instanceof Error ? e.message : "Delete failed");
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div
      className={`card h-full overflow-hidden flex flex-col transition border ${
        selected
          ? "border-accent-500/60 ring-2 ring-accent-500/40 bg-accent-500/[0.04]"
          : "border-white/5 hover:border-white/10 hover:bg-white/[0.02]"
      } ${allDeleted ? "opacity-60" : ""}`}
    >
      {/* Cover photo with overlays */}
      <div className="relative aspect-[16/11] bg-white/[0.03] group/cover">
        {p.cover_photo_url ? (
          <button type="button" onClick={onOpenPhoto}
            className="absolute inset-0 focus:outline-none"
            aria-label="Open full-size image">
            <img src={p.cover_photo_url} alt="" loading="lazy"
              className="w-full h-full object-cover transition-transform duration-300 group-hover/cover:scale-[1.03]" />
          </button>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-slate-700 text-3xl">—</div>
        )}

        {/* Top-left: selection checkbox */}
        {selectable && (
          <label
            onClick={(e) => e.stopPropagation()}
            className={`absolute top-2 left-2 w-7 h-7 rounded-md flex items-center justify-center backdrop-blur-sm transition cursor-pointer ${
              selected ? "bg-accent-500/90 text-white" : "bg-black/40 text-white/80 opacity-0 group-hover/cover:opacity-100"
            }`}
            title={selected ? "Deselect" : "Select"}
          >
            <input type="checkbox" checked={selected} onChange={onToggleSelect}
              className="sr-only" />
            {selected ? (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5"
                strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
            ) : (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="3" /></svg>
            )}
          </label>
        )}

        {/* Top-right: platform chips + photo count */}
        <div className="absolute top-2 right-2 flex items-center gap-1.5">
          {p.photo_count && p.photo_count > 0 && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-black/50 text-white/90 text-[10px] backdrop-blur-sm">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" />
              </svg>
              {p.photo_count}
            </span>
          )}
          <PlatformChips p={p} />
        </div>

        {/* Bottom-left: price */}
        <div className="absolute bottom-2 left-2 right-2 flex items-end justify-between gap-2">
          <div className="text-white text-lg font-semibold tabular-nums drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
            {formatPrice(p.price, p.currency)}
          </div>
          {formattedOffer && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-emerald-500/85 text-white text-[11px] font-semibold tabular-nums shadow">
              {formattedOffer}
            </span>
          )}
        </div>

        {p.is_manual && (
          <span className="absolute top-2 left-2 ml-9 px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wider text-white/90 bg-black/50 backdrop-blur-sm"
            title="Manual placement — logged outside the publish pipeline">
            Manual
          </span>
        )}
        {allDeleted && (
          <span className="absolute inset-x-0 top-1/2 -translate-y-1/2 mx-auto w-max px-2 py-1 rounded text-[11px] font-semibold uppercase tracking-wider text-rose-100 bg-rose-600/70 backdrop-blur-sm"
            title="Deleted on the portals">
            Deleted on portals
          </span>
        )}
      </div>

      {/* Body */}
      <div className="p-3 flex-1 flex flex-col gap-2">
        <a href={primaryUrl} target="_blank" rel="noopener noreferrer"
          className="text-slate-100 hover:text-accent-300 text-[13px] font-medium leading-snug line-clamp-2"
          title={p.title || primaryUrl}>
          {shortTitle}
        </a>

        <div className="text-[12px] text-slate-400 leading-snug">{specsLine(p) || <span className="text-slate-600">—</span>}</div>

        {(p.district || p.street) && (
          <div className="flex items-center gap-1 text-[12px] text-slate-400">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round" className="text-slate-500">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
            </svg>
            <span className="truncate">
              {[p.district, p.street].filter(Boolean).join(" · ")}
            </span>
          </div>
        )}

        {p.description && <DescriptionBlock text={p.description} />}

        <div className="flex flex-col gap-1 mt-auto pt-2 border-t border-white/5">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold w-12 flex-shrink-0">Owner</span>
            <span className="text-[12px] text-slate-300 truncate flex-1" title={p.owner_name || ""}>
              {p.owner_name || <span className="text-slate-600 italic">—</span>}
            </span>
          </div>
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold w-12 flex-shrink-0">Phone</span>
            <PhoneEditor placementIds={groupIds} value={p.owner_phone} canEdit={canEditPhone} onSaved={onUpdatePhone} />
          </div>
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold w-12 flex-shrink-0">Offer</span>
            <OfferEditor placement={p} canEdit={canEditOffer} onSaved={onUpdateOffer} />
          </div>
          <div className="flex items-start gap-2 min-w-0">
            <span className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold w-12 flex-shrink-0 pt-0.5">Notes</span>
            <div className="flex-1 min-w-0">
              <NotesEditor
                placementIds={groupIds}
                value={p.notes}
                canEdit={canEditPhone}
                onSaved={onUpdateNotes}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="px-3 py-2 border-t border-white/5 flex flex-col gap-2 text-[11px] text-slate-500 bg-white/[0.01]">
        {/* Identity + when */}
        <div className="flex items-center gap-2">
          {p.user && (
            <Avatar
              url={p.user.avatar_url}
              name={p.user.full_name}
              email={p.user.email}
              size="xs"
            />
          )}
          <div className="min-w-0 flex-1 truncate text-slate-400">
            {p.user?.full_name || p.user?.email || "—"}
          </div>
          <span className="text-slate-600" title={formatExactDate(p.published_at)}>
            {relativeTime(p.published_at)}
          </span>
        </div>

        {/* Open actions: the source listing + our placed listing per portal */}
        <div className="flex items-center gap-1.5 flex-wrap">
          {p.source_url && (
            <a href={p.source_url} target="_blank" rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-white/10 text-[11px] font-medium text-slate-300 hover:text-white hover:bg-white/[0.04]"
              title={`Open source listing: ${p.source_url}`}>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" />
                <path d="M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5" />
              </svg>
              Source
            </a>
          )}
          {pubs.map((pp) => {
            const deleted = Boolean(pp.deleted_at);
            const label = pp.platform === "myhome" ? "MyHome" : "SS";
            return (
              <a key={pp.id} href={pp.published_url} target="_blank" rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[11px] font-medium ${
                  deleted
                    ? "border-white/5 text-slate-600 line-through hover:text-slate-400"
                    : "border-accent-500/30 text-accent-300 hover:text-accent-200 hover:bg-accent-500/10"
                }`}
                title={deleted ? `${label}: deleted on portal (link may 404)` : `Open on ${label}`}>
                {label} ↗
              </a>
            );
          })}
          <div className="flex-1" />
          {canPortalDelete && hasExternal && !allDeleted && (
            <button
              type="button"
              onClick={() => { setConfirming(true); setDelErr(null); }}
              title="Delete this listing on MyHome + SS"
              className="px-1.5 py-0.5 rounded text-[11px] font-medium text-rose-300/80 hover:text-rose-200 hover:bg-rose-500/10"
            >
              Delete
            </button>
          )}
        </div>
      </div>

      {/* Inline portal-delete confirm — irreversible, so type-to-confirm. */}
      {confirming && (
        <div className="px-3 py-2 border-t border-rose-500/25 bg-rose-500/[0.05] space-y-2">
          <div className="text-[11px] text-rose-200">
            Delete this listing on{" "}
            {deletablePlatforms.map((x) => (x.platform === "myhome" ? "MyHome" : "SS")).join(" + ") || "the portals"}? This removes it from the live site,
            blacklists the source listing so bulk-publish never re-uploads it,
            and removes it from the market.
          </div>
          {delErr && <div className="text-[11px] text-rose-300">{delErr}</div>}
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-rose-300">
              Type <code className="px-1 rounded bg-white/5">delete</code>:
            </span>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              disabled={deleting}
              autoFocus
              onKeyDown={(e) => { if (e.key === "Escape") { setConfirming(false); setConfirmText(""); } }}
              className="input py-1 px-2 text-xs w-24"
            />
            <button
              type="button"
              onClick={runDelete}
              disabled={deleting || confirmText !== "delete"}
              className="text-xs px-2 py-1 rounded bg-rose-500/20 text-rose-300 hover:bg-rose-500/30 disabled:opacity-40"
            >
              {deleting ? "Deleting…" : "Delete"}
            </button>
            <button
              type="button"
              onClick={() => { setConfirming(false); setConfirmText(""); setDelErr(null); }}
              disabled={deleting}
              className="text-xs px-2 py-1 rounded text-slate-400 hover:text-slate-200"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- Bulk action bar ----------

function BulkBar({
  count,
  canDelete,
  onSetOffer,
  onClearOffer,
  onDelete,
  onShare,
  onClear,
}: {
  count: number;
  canDelete: boolean;
  onSetOffer: (kind: OfferKind, value: number) => Promise<void>;
  onClearOffer: () => Promise<void>;
  onDelete: () => Promise<void>;
  onShare: () => void;
  onClear: () => void;
}) {
  const [mode, setMode] = useState<null | "offer" | "delete">(null);
  const [kind, setKind] = useState<OfferKind>("percent");
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState("");

  async function applyOffer() {
    setErr(null);
    const n = Number(value.trim());
    if (!Number.isFinite(n) || n < 0) { setErr("Bad number"); return; }
    if (kind === "percent" && n > 100) { setErr("0–100"); return; }
    setBusy(true);
    try {
      await onSetOffer(kind, n);
      setMode(null); setValue("");
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed");
    } finally {
      setBusy(false);
    }
  }

  async function applyClearOffer() {
    setBusy(true);
    setErr(null);
    try {
      await onClearOffer();
      setMode(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed");
    } finally {
      setBusy(false);
    }
  }

  async function applyDelete() {
    setBusy(true);
    setErr(null);
    try {
      await onDelete();
      setMode(null); setDeleteConfirm("");
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 max-w-[calc(100vw-2rem)]">
      <div className="card px-3 py-2.5 border border-white/15 bg-ink-900/95 backdrop-blur-md shadow-2xl flex items-center gap-3 flex-wrap">
        <span className="text-[12px] font-semibold text-slate-200 tabular-nums">
          {count} selected
        </span>
        <span className="w-px h-5 bg-white/10" />

        {mode === null && (
          <>
            <button type="button" onClick={onShare}
              className="btn-secondary text-xs px-2 py-1">📤 Share</button>
            <button type="button" onClick={() => setMode("offer")}
              className="btn-secondary text-xs px-2 py-1">Set commission</button>
            {canDelete && (
              <button type="button" onClick={() => setMode("delete")}
                className="text-xs px-2 py-1 rounded text-rose-300 hover:bg-rose-500/15">Delete</button>
            )}
            <button type="button" onClick={onClear}
              className="text-xs px-2 py-1 rounded text-slate-400 hover:text-slate-200">Clear</button>
          </>
        )}

        {mode === "offer" && (
          <>
            <select value={kind} onChange={(e) => setKind(e.target.value as OfferKind)} disabled={busy}
              className="input py-1 px-1.5 text-xs">
              <option value="percent">%</option>
              <option value="fixed">amt</option>
            </select>
            <input type="number" step="0.01" min="0" value={value} onChange={(e) => setValue(e.target.value)}
              disabled={busy} autoFocus
              onKeyDown={(e) => { if (e.key === "Enter") applyOffer(); if (e.key === "Escape") setMode(null); }}
              placeholder={kind === "percent" ? "2.5" : "500"}
              className="input py-1 px-2 text-xs w-24 tabular-nums" />
            <button type="button" onClick={applyOffer} disabled={busy || !value.trim()}
              className="btn-primary text-xs px-2 py-1 disabled:opacity-50">Apply</button>
            <button type="button" onClick={applyClearOffer} disabled={busy}
              className="text-xs px-2 py-1 rounded text-rose-300 hover:bg-rose-500/15"
              title="Clear commission on selected">Clear offer</button>
            <button type="button" onClick={() => { setMode(null); setErr(null); }} disabled={busy}
              className="text-xs px-2 py-1 rounded text-slate-400 hover:text-slate-200">Cancel</button>
            {err && <span className="text-[11px] text-rose-400">{err}</span>}
          </>
        )}

        {mode === "delete" && (
          <>
            <span className="text-[11px] text-rose-300">Deletes the records, blacklists their source listings (no more bulk re-uploads), and removes them from the market. Type <code className="px-1 rounded bg-white/5">delete</code> to confirm:</span>
            <input type="text" value={deleteConfirm} onChange={(e) => setDeleteConfirm(e.target.value)}
              disabled={busy} autoFocus
              onKeyDown={(e) => { if (e.key === "Escape") setMode(null); }}
              className="input py-1 px-2 text-xs w-24" />
            <button type="button" onClick={applyDelete}
              disabled={busy || deleteConfirm !== "delete"}
              className="text-xs px-2 py-1 rounded bg-rose-500/20 text-rose-300 hover:bg-rose-500/30 disabled:opacity-40">
              Delete {count}
            </button>
            <button type="button" onClick={() => { setMode(null); setDeleteConfirm(""); setErr(null); }} disabled={busy}
              className="text-xs px-2 py-1 rounded text-slate-400 hover:text-slate-200">Cancel</button>
            {err && <span className="text-[11px] text-rose-400">{err}</span>}
          </>
        )}
      </div>
    </div>
  );
}

// ---------- Pagination ----------

const PAGE_SIZE_OPTIONS = [24, 48, 96];

function Pagination({
  page, totalPages, pageSize, total, windowStart, windowEnd, loading,
  onPageChange, onPageSizeChange,
}: {
  page: number; totalPages: number; pageSize: number; total: number;
  windowStart: number; windowEnd: number; loading: boolean;
  onPageChange: (p: number) => void; onPageSizeChange: (n: number) => void;
}) {
  const canPrev = page > 1 && !loading;
  const canNext = page < totalPages && !loading;
  const pages: (number | "…")[] = [];
  if (totalPages <= 7) for (let i = 1; i <= totalPages; i++) pages.push(i);
  else {
    const nearby = new Set([1, totalPages, page - 1, page, page + 1]);
    let last = 0;
    for (let i = 1; i <= totalPages; i++) {
      if (nearby.has(i)) { if (i - last > 1) pages.push("…"); pages.push(i); last = i; }
    }
  }
  return (
    <div className="mt-4 flex flex-wrap items-center gap-3 text-xs text-slate-500">
      <div className="tabular-nums">
        {total === 0 ? "No results" : `Showing ${windowStart.toLocaleString()}–${windowEnd.toLocaleString()} of ${total.toLocaleString()}`}
      </div>
      <div className="ml-auto flex items-center gap-1">
        <button type="button" onClick={() => onPageChange(1)} disabled={!canPrev}
          className="px-2 py-1 rounded hover:bg-white/[0.04] disabled:opacity-30 disabled:cursor-not-allowed" title="First">«</button>
        <button type="button" onClick={() => onPageChange(page - 1)} disabled={!canPrev}
          className="px-2 py-1 rounded hover:bg-white/[0.04] disabled:opacity-30 disabled:cursor-not-allowed">‹ Prev</button>
        <div className="flex items-center gap-0.5 px-1 tabular-nums">
          {pages.map((p, i) => p === "…" ? (
            <span key={`e${i}`} className="px-1 text-slate-600">…</span>
          ) : (
            <button key={p} type="button" onClick={() => onPageChange(p)} disabled={loading}
              className={`min-w-[28px] px-2 py-1 rounded transition ${
                p === page ? "bg-accent-500/20 text-accent-300 font-semibold" : "text-slate-400 hover:bg-white/[0.04] hover:text-slate-200"
              } disabled:opacity-50`}
              aria-current={p === page ? "page" : undefined}>{p}</button>
          ))}
        </div>
        <button type="button" onClick={() => onPageChange(page + 1)} disabled={!canNext}
          className="px-2 py-1 rounded hover:bg-white/[0.04] disabled:opacity-30 disabled:cursor-not-allowed">Next ›</button>
        <button type="button" onClick={() => onPageChange(totalPages)} disabled={!canNext}
          className="px-2 py-1 rounded hover:bg-white/[0.04] disabled:opacity-30 disabled:cursor-not-allowed" title="Last">»</button>
      </div>
      <div className="flex items-center gap-2">
        <span>Per page</span>
        <select value={pageSize} onChange={(e) => onPageSizeChange(Number(e.target.value))} disabled={loading}
          className="input py-1 px-2 text-xs">
          {PAGE_SIZE_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
      </div>
    </div>
  );
}

// ---------- Main page ----------

interface PlacementList { items: Placement[]; total: number; limit: number; offset: number; }

type DatePreset = "all" | "7d" | "30d" | "90d";

const ROOM_OPTIONS = [
  { label: "1", rooms: "1", roomsMin: null as number | null },
  { label: "2", rooms: "2", roomsMin: null },
  { label: "3", rooms: "3", roomsMin: null },
  { label: "4", rooms: "4", roomsMin: null },
  { label: "5+", rooms: "", roomsMin: 5 },
];

// Deal-type options for the pill dropdown. Empty value = "all"; the rest
// mirror the ``Placement.deal_type`` vocab the backend validates against.
const DEAL_OPTIONS = [
  { value: "", label: "All deals" },
  { value: "sale", label: "Sale" },
  { value: "rent", label: "Rent" },
  { value: "mortgage", label: "Mortgage" },
];

// Building-status / condition facets reuse Market's canonical code→label
// lists (single source of truth) so the captions match every other page.
// English glosses are shown since the rest of this page's chrome is English.
const BUILDING_FACETS = BUILDING_STATUS_OPTIONS.map((o) => ({
  value: o.value,
  label: o.en,
}));
const CONDITION_FACETS = CONDITION_OPTIONS.map((o) => ({
  value: o.value,
  label: o.en,
}));

// Toolbar controls — the two choices that stay visible next to the search box.
const PLATFORM_OPTIONS = [
  { value: "", label: "Both" },
  { value: "myhome", label: "MyHome" },
  { value: "ss", label: "SS.ge" },
];
const SORT_OPTIONS: { value: PlacementSort; label: string }[] = [
  { value: "published_desc", label: "Newest" },
  { value: "published_asc", label: "Oldest" },
  { value: "price_desc", label: "Price ↓" },
  { value: "price_asc", label: "Price ↑" },
];

const DATE_OPTIONS: { value: DatePreset; label: string }[] = [
  { value: "all", label: "All time" },
  { value: "7d", label: "Last 7d" },
  { value: "30d", label: "Last 30d" },
  { value: "90d", label: "Last 90d" },
];

export type PriceCcy = "usd" | "gel";

// Placements are stored one price + one currency per listing, and the table is
// genuinely mixed — ~49k USD rows against ~5k GEL. The filter therefore names
// the currency it is filtering IN, and the backend restates every row into it
// using the day's NBG rate. The presets differ per currency for the same
// reason: ₾100–200k is a much cheaper flat than $100–200k.
const PRICE_PRESETS_USD: RangePreset[] = [
  { label: "< $50k", min: "", max: "50000" },
  { label: "$50–100k", min: "50000", max: "100000" },
  { label: "$100–200k", min: "100000", max: "200000" },
  { label: "$200k+", min: "200000", max: "" },
];
const PRICE_PRESETS_GEL: RangePreset[] = [
  { label: "< ₾150k", min: "", max: "150000" },
  { label: "₾150–300k", min: "150000", max: "300000" },
  { label: "₾300–500k", min: "300000", max: "500000" },
  { label: "₾500k+", min: "500000", max: "" },
];
const AREA_PRESETS: RangePreset[] = [
  { label: "< 40 m²", min: "", max: "40" },
  { label: "40–70", min: "40", max: "70" },
  { label: "70–120", min: "70", max: "120" },
  { label: "120 m²+", min: "120", max: "" },
];

/** Everything that isn't in the toolbar lives here — one labelled section per
 *  filter group, all inline controls (no nested popovers). Drives the live
 *  filter state directly; there is no draft/apply step. */
function PlacementFiltersPanel({
  onClose,
  total,
  filterCount,
  onClear,
  members,
  showEmployeeFilter,
  districts,
  streets,
  state,
}: {
  onClose: () => void;
  total: number;
  filterCount: number;
  onClear: () => void;
  members: TeamMember[];
  showEmployeeFilter: boolean;
  districts: PlacementDistrict[];
  streets: PlacementStreet[];
  state: PlacementFilterState;
}) {
  const s = state;
  const districtFacets: FacetOption[] = districts.map((d) => ({
    value: d.district,
    count: d.count,
  }));
  const streetFacets: FacetOption[] = streets.map((st) => ({
    value: st.street,
    count: st.count,
  }));

  return (
    <FiltersDrawer
      onClose={onClose}
      count={filterCount}
      resultTotal={total}
      resultNoun="placement"
      onClear={onClear}
    >
      <FilterSection title="Quick filters">
        <ToggleRow
          items={[
            { label: "My listings", checked: s.quick.myListings, onChange: () => s.toggleQuick("myListings") },
            { label: "This week", checked: s.quick.thisWeek, onChange: () => s.toggleQuick("thisWeek") },
            { label: "Missing phone", checked: s.quick.missingPhone, onChange: () => s.toggleQuick("missingPhone") },
            { label: "Missing offer", checked: s.quick.missingOffer, onChange: () => s.toggleQuick("missingOffer") },
            { label: "Manual only", checked: s.quick.manualOnly, onChange: () => s.toggleQuick("manualOnly") },
          ]}
        />
      </FilterSection>

      {showEmployeeFilter && (
        <FilterSection title="Employee">
          <select
            className="input !py-1.5 !text-[12px] w-full"
            value={s.userId}
            onChange={(e) => s.setUserId(e.target.value)}
            disabled={s.quick.myListings}
            title={s.quick.myListings ? "“My listings” quick filter is on" : undefined}
          >
            <option value="">All visible</option>
            {members.map((m) => (
              <option key={m.id} value={m.id}>{m.full_name || m.email}</option>
            ))}
          </select>
        </FilterSection>
      )}

      <FilterSection title="Deal type">
        <div className="flex flex-wrap gap-1.5">
          {DEAL_OPTIONS.map((o) => (
            <FilterChip
              key={o.value || "all"}
              active={s.dealType === o.value}
              onClick={() => s.setDealType(o.value)}
            >
              {o.label}
            </FilterChip>
          ))}
        </div>
      </FilterSection>

      <FilterSection title="Property type">
        <div className="flex flex-wrap gap-1.5">
          <FilterChip active={s.selectedTypes.length === 0} onClick={() => s.setSelectedTypes([])}>
            Any
          </FilterChip>
          {PROPERTY_TYPES.map((t) => {
            const on = s.selectedTypes.includes(t.id);
            return (
              <FilterChip
                key={t.id}
                active={on}
                onClick={() =>
                  s.setSelectedTypes(
                    on ? s.selectedTypes.filter((x) => x !== t.id) : [...s.selectedTypes, t.id],
                  )
                }
              >
                <span className="mr-1">{t.icon}</span>
                {t.label}
              </FilterChip>
            );
          })}
        </div>
      </FilterSection>

      <FilterSection title="Rooms">
        <div className="flex flex-wrap gap-1.5">
          <FilterChip active={s.selectedRooms.length === 0} onClick={() => s.setSelectedRooms([])}>
            Any
          </FilterChip>
          {ROOM_OPTIONS.map((r) => {
            const on = s.selectedRooms.includes(r.label);
            return (
              <FilterChip
                key={r.label}
                active={on}
                onClick={() =>
                  s.setSelectedRooms(
                    on ? s.selectedRooms.filter((x) => x !== r.label) : [...s.selectedRooms, r.label],
                  )
                }
              >
                <span className="tabular-nums">{r.label}</span>
              </FilterChip>
            );
          })}
        </div>
      </FilterSection>

      <FilterSection
        title="Price"
        aside={
          <div className="inline-flex rounded-md border border-white/10 overflow-hidden text-[11px] font-semibold">
            {(["usd", "gel"] as PriceCcy[]).map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => s.setPriceCcy(c)}
                className={`px-2.5 py-1 transition ${
                  s.priceCcy === c
                    ? "bg-white/10 text-slate-100"
                    : "text-slate-500 hover:text-slate-300"
                }`}
              >
                {c === "usd" ? "$ USD" : "₾ GEL"}
              </button>
            ))}
          </div>
        }
      >
        <InlineRange
          min={s.minPrice}
          max={s.maxPrice}
          onMin={s.setMinPrice}
          onMax={s.setMaxPrice}
          prefix={s.priceCcy === "usd" ? "$" : "₾"}
        />
        <PresetChips
          presets={s.priceCcy === "usd" ? PRICE_PRESETS_USD : PRICE_PRESETS_GEL}
          min={s.minPrice}
          max={s.maxPrice}
          onMin={s.setMinPrice}
          onMax={s.setMaxPrice}
        />
      </FilterSection>

      <FilterSection title="Area (m²)">
        <InlineRange min={s.minArea} max={s.maxArea} onMin={s.setMinArea} onMax={s.setMaxArea} suffix="m²" />
        <PresetChips
          presets={AREA_PRESETS}
          min={s.minArea}
          max={s.maxArea}
          onMin={s.setMinArea}
          onMax={s.setMaxArea}
        />
      </FilterSection>

      <FilterSection title="Floor">
        <InlineRange min={s.floorMin} max={s.floorMax} onMin={s.setFloorMin} onMax={s.setFloorMax} />
      </FilterSection>

      <FilterSection title="Building status">
        <InlineMultiSelect
          label="Building status"
          options={BUILDING_FACETS}
          selected={s.buildingStatuses}
          onChange={s.setBuildingStatuses}
        />
      </FilterSection>

      <FilterSection title="Condition">
        <InlineMultiSelect
          label="Condition"
          options={CONDITION_FACETS}
          selected={s.conditions}
          onChange={s.setConditions}
        />
      </FilterSection>

      <FilterSection title="District">
        <InlineMultiSelect
          label="District"
          options={districtFacets}
          selected={s.selectedDistricts}
          onChange={s.setSelectedDistricts}
        />
      </FilterSection>

      <FilterSection title="Street">
        <InlineMultiSelect
          label="Street"
          options={streetFacets}
          selected={s.selectedStreets}
          onChange={s.setSelectedStreets}
        />
      </FilterSection>

      <FilterSection title="Published within">
        <div className="flex flex-wrap gap-1.5">
          {DATE_OPTIONS.map((o) => (
            <FilterChip
              key={o.value}
              active={s.datePreset === o.value}
              onClick={() => s.setDatePreset(o.value)}
            >
              {o.label}
            </FilterChip>
          ))}
        </div>
      </FilterSection>
    </FiltersDrawer>
  );
}

/** The filter state bundle handed to the drawer — every setter the page owns,
 *  passed through so the panel stays presentational. */
interface PlacementFilterState {
  userId: string;
  setUserId: (v: string) => void;
  dealType: string;
  setDealType: (v: string) => void;
  selectedTypes: string[];
  setSelectedTypes: (v: string[]) => void;
  selectedRooms: string[];
  setSelectedRooms: (v: string[]) => void;
  minPrice: string;
  maxPrice: string;
  setMinPrice: (v: string) => void;
  setMaxPrice: (v: string) => void;
  priceCcy: PriceCcy;
  setPriceCcy: (v: PriceCcy) => void;
  minArea: string;
  maxArea: string;
  setMinArea: (v: string) => void;
  setMaxArea: (v: string) => void;
  floorMin: string;
  floorMax: string;
  setFloorMin: (v: string) => void;
  setFloorMax: (v: string) => void;
  buildingStatuses: string[];
  setBuildingStatuses: (v: string[]) => void;
  conditions: string[];
  setConditions: (v: string[]) => void;
  selectedDistricts: string[];
  setSelectedDistricts: (v: string[]) => void;
  selectedStreets: string[];
  setSelectedStreets: (v: string[]) => void;
  datePreset: DatePreset;
  setDatePreset: (v: DatePreset) => void;
  quick: QuickFilter;
  toggleQuick: (k: keyof QuickFilter) => void;
}

export function Placements() {
  const { user } = useAuth();

  const [rows, setRows] = useState<Placement[] | null>(null);
  const [total, setTotal] = useState(0);
  const [loadingPage, setLoadingPage] = useState(false);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [districts, setDistricts] = useState<PlacementDistrict[]>([]);
  const [streets, setStreets] = useState<PlacementStreet[]>([]);
  const [stats, setStats] = useState<PlacementStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lightbox, setLightbox] = useState<{ src: string; alt: string } | null>(null);
  const [showManualModal, setShowManualModal] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [searchParams] = useSearchParams();

  // ---- filters ----
  const [userId, setUserId] = useState<string>(() => searchParams.get("user") || "");
  const [platform, setPlatform] = useState<"" | Platform>("");
  const [search, setSearch] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [dealType, setDealType] = useState<string>("");
  const [selectedRooms, setSelectedRooms] = useState<string[]>([]);
  const [minPrice, setMinPrice] = useState<string>("");
  const [maxPrice, setMaxPrice] = useState<string>("");
  const [priceCcy, setPriceCcy] = useState<PriceCcy>("usd");
  const [minArea, setMinArea] = useState<string>("");
  const [maxArea, setMaxArea] = useState<string>("");
  const [floorMin, setFloorMin] = useState<string>("");
  const [floorMax, setFloorMax] = useState<string>("");
  const [buildingStatuses, setBuildingStatuses] = useState<string[]>([]);
  const [conditions, setConditions] = useState<string[]>([]);
  const [datePreset, setDatePreset] = useState<DatePreset>("all");
  const [selectedDistricts, setSelectedDistricts] = useState<string[]>([]);
  const [selectedStreets, setSelectedStreets] = useState<string[]>([]);
  const [sort, setSort] = useState<PlacementSort>("published_desc");
  // ``?missing=phone|offer`` seeds the matching quick filter — the Home
  // page's "4 listings missing a phone" card links straight into the
  // filtered list rather than dropping the user on an unfiltered page.
  const [quick, setQuick] = useState<QuickFilter>(() => {
    const missing = searchParams.get("missing");
    return {
      myListings: false,
      missingPhone: missing === "phone",
      missingOffer: missing === "offer",
      manualOnly: false,
      thisWeek: false,
    };
  });

  // ---- selection ----
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // ---- share links (selection → public buyer page) ----
  const share = useShareLinks();
  const [sharePrepareOpen, setSharePrepareOpen] = useState(false);
  // Same dialog with no selection: the broker pastes MyHome / ss.ge URLs they
  // already have in hand and gets the same public brochure link.
  const [urlShareOpen, setUrlShareOpen] = useState(false);
  // Link currently open in the edit modal (add/remove/reorder cards, fix
  // details) — null when closed.
  const [editLinkId, setEditLinkId] = useState<string | null>(null);

  // ---- paging ----
  const [pageSize, setPageSize] = useState<number>(() => {
    const stored = Number(localStorage.getItem("placements.pageSize"));
    return PAGE_SIZE_OPTIONS.includes(stored) ? stored : 48;
  });
  const [page, setPage] = useState(1);

  // Debounce search input — keeps the user from kicking off a request on
  // every keystroke; we want a single fetch when they pause typing.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Reset paging whenever a filter changes. Without this, jumping from page
  // 4 of "all" to a tight filter would land on an empty middle page.
  useEffect(() => {
    setPage(1);
    setSelected(new Set());
  }, [
    userId, platform, debouncedSearch, pageSize, selectedTypes.join(","), selectedRooms.join(","),
    minPrice, maxPrice, priceCcy, datePreset, selectedDistricts.join(","),
    selectedStreets.join(","), sort,
    dealType, minArea, maxArea, floorMin, floorMax,
    buildingStatuses.join(","), conditions.join(","),
    quick.myListings, quick.missingPhone, quick.missingOffer, quick.manualOnly, quick.thisWeek,
  ]);

  useEffect(() => { localStorage.setItem("placements.pageSize", String(pageSize)); }, [pageSize]);

  function toggleQuick(k: keyof QuickFilter) {
    setQuick((q) => ({ ...q, [k]: !q[k] }));
  }

  // Build the shared query string used by the list, stats, and CSV export.
  // Stats omits the missing-* / manual-* quick flags so the tiles always
  // show the absolute counts (otherwise toggling a tile would zero out
  // its own number).
  function baseFilters(): URLSearchParams {
    const qs = new URLSearchParams();
    const effectiveUserId = quick.myListings && user ? user.id : userId;
    if (effectiveUserId) qs.set("user_id", effectiveUserId);
    if (platform) qs.set("platform", platform);
    if (debouncedSearch) qs.set("q", debouncedSearch);
    if (selectedTypes.length) qs.set("property_type", selectedTypes.join(","));
    if (selectedRooms.length) {
      const exacts: string[] = [];
      let minPlus: number | null = null;
      for (const k of selectedRooms) {
        const opt = ROOM_OPTIONS.find((r) => r.label === k);
        if (!opt) continue;
        if (opt.roomsMin != null) minPlus = opt.roomsMin;
        if (opt.rooms) exacts.push(opt.rooms);
      }
      if (exacts.length) qs.set("rooms", exacts.join(","));
      if (minPlus != null) qs.set("rooms_min", String(minPlus));
    }
    if (dealType) qs.set("deal_type", dealType);
    if (minPrice.trim()) qs.set("min_price", minPrice.trim());
    if (maxPrice.trim()) qs.set("max_price", maxPrice.trim());
    // Always sent: it tells the backend which currency the bounds are IN, so
    // it can restate every row's own currency before comparing.
    if (minPrice.trim() || maxPrice.trim()) qs.set("price_ccy", priceCcy);
    if (minArea.trim()) qs.set("min_area", minArea.trim());
    if (maxArea.trim()) qs.set("max_area", maxArea.trim());
    if (floorMin.trim()) qs.set("floor_min", floorMin.trim());
    if (floorMax.trim()) qs.set("floor_max", floorMax.trim());
    if (buildingStatuses.length) qs.set("building_status", buildingStatuses.join(","));
    if (conditions.length) qs.set("condition", conditions.join(","));
    if (datePreset !== "all" || quick.thisWeek) {
      const days = quick.thisWeek ? 7 : datePreset === "7d" ? 7 : datePreset === "30d" ? 30 : 90;
      const from = new Date(Date.now() - days * 86400 * 1000).toISOString();
      qs.set("from", from);
    }
    if (selectedDistricts.length) qs.set("district", selectedDistricts.join(","));
    if (selectedStreets.length) qs.set("street", selectedStreets.join(","));
    if (quick.manualOnly) qs.set("is_manual", "true");
    if (quick.missingPhone) qs.set("missing_phone", "true");
    if (quick.missingOffer) qs.set("missing_offer", "true");
    return qs;
  }

  const reload = useCallback(async () => {
    setLoadingPage(true);
    try {
      const qs = baseFilters();
      qs.set("limit", String(pageSize));
      qs.set("offset", String((page - 1) * pageSize));
      qs.set("sort", sort);
      const data = await api<PlacementList>(`/api/placements?${qs.toString()}`);
      setRows(data.items);
      setTotal(data.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load placements");
    } finally {
      setLoadingPage(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    userId, platform, debouncedSearch, pageSize, page, sort,
    selectedTypes.join(","), selectedRooms.join(","), minPrice, maxPrice, datePreset,
    dealType, minArea, maxArea, floorMin, floorMax,
    buildingStatuses.join(","), conditions.join(","),
    selectedDistricts.join(","), selectedStreets.join(","),
    quick.myListings, quick.missingPhone, quick.missingOffer,
    quick.manualOnly, quick.thisWeek,
  ]);

  const reloadStats = useCallback(async () => {
    try {
      const qs = baseFilters();
      // Strip the quick-toggle flags so each tile shows the absolute count
      // and clicking one doesn't make its own number disappear.
      qs.delete("missing_phone");
      qs.delete("missing_offer");
      qs.delete("is_manual");
      qs.delete("from");
      const data = await api<PlacementStats>(`/api/placements/stats?${qs.toString()}`);
      setStats(data);
    } catch {
      /* stats tile non-critical; ignore so the page still renders */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    userId, platform, debouncedSearch,
    selectedTypes.join(","), selectedRooms.join(","), minPrice, maxPrice, datePreset,
    dealType, minArea, maxArea, floorMin, floorMax,
    buildingStatuses.join(","), conditions.join(","),
    selectedDistricts.join(","), selectedStreets.join(","), quick.myListings,
  ]);

  useEffect(() => {
    api<TeamMember[]>("/api/team").then(setMembers).catch(() => setMembers([]));
    api<PlacementDistrict[]>("/api/placements/districts").then(setDistricts).catch(() => setDistricts([]));
    api<PlacementStreet[]>("/api/placements/streets").then(setStreets).catch(() => setStreets([]));
  }, []);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => { reloadStats(); }, [reloadStats]);

  function canEditOffer(p: Placement): boolean {
    if (!user) return false;
    if (user.role === "shareholder" || user.role === "executive_manager" || user.role === "operations_manager" || user.role === "lead_broker") return true;
    return p.user?.id === user.id;
  }
  function canEditPhone(p: Placement): boolean {
    return canEditOffer(p);
  }
  const canDelete = user?.role === "shareholder" || user?.role === "executive_manager" || user?.role === "operations_manager";

  const showEmployeeFilter = members.length > 1;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const windowStart = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const windowEnd = Math.min(page * pageSize, total);

  function buildCsvUrl() {
    const qs = baseFilters();
    const s = qs.toString();
    return `/api/placements.csv${s ? "?" + s : ""}`;
  }

  function resetAllFilters() {
    setUserId("");
    setPlatform("");
    setSearch("");
    setSelectedTypes([]);
    setDealType("");
    setSelectedRooms([]);
    setMinPrice("");
    setMaxPrice("");
    setMinArea("");
    setMaxArea("");
    setFloorMin("");
    setFloorMax("");
    setBuildingStatuses([]);
    setConditions([]);
    setDatePreset("all");
    setSelectedDistricts([]);
    setSelectedStreets([]);
    setSort("published_desc");
    setQuick({ myListings: false, missingPhone: false, missingOffer: false, manualOnly: false, thisWeek: false });
  }

  // Selection helpers — toggle one, select-all-on-page, clear.
  function toggleOne(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  function selectAllOnPage() {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const r of rows ?? []) next.add(r.id);
      return next;
    });
  }
  function clearSelection() { setSelected(new Set()); }

  // Bulk endpoints operate on every platform publication in each selected
  // group — so a row that represents MH + SS sends both ids. That matches
  // the inline editors which already fan out across the group.
  function selectedPlatformIds(): string[] {
    const out: string[] = [];
    const byId = new Map((rows ?? []).map((r) => [r.id, r]));
    for (const id of selected) {
      const r = byId.get(id);
      if (!r) continue;
      const ids = r.platforms.length ? r.platforms.map((x) => x.id) : [r.id];
      out.push(...ids);
    }
    return out;
  }

  async function bulkSetOffer(kind: OfferKind, value: number) {
    await api("/api/placements/bulk-offer", {
      method: "POST",
      json: { ids: selectedPlatformIds(), offer_kind: kind, offer_value: value },
    });
    clearSelection();
    await Promise.all([reload(), reloadStats()]);
  }
  async function bulkClearOffer() {
    await api("/api/placements/bulk-offer", {
      method: "POST",
      json: { ids: selectedPlatformIds(), offer_kind: null, offer_value: null },
    });
    clearSelection();
    await Promise.all([reload(), reloadStats()]);
  }
  async function bulkDelete() {
    await api("/api/placements/bulk-delete", {
      method: "POST",
      json: { ids: selectedPlatformIds() },
    });
    clearSelection();
    await Promise.all([reload(), reloadStats()]);
  }
  // Generate a public share page from the current selection. Each card maps
  // to one ``kind: "placement"`` selection (the representative row id) — the
  // backend snapshots that placement and rehosts its photos in the
  // background, same path the Market share flow uses.
  async function generateShare(opts: {
    agentPhone: string;
    language: "ka" | "ru" | "en";
    lifetimeDays: number;
    // URLs the broker pasted in the dialog — carried through so they land on
    // the same brochure as the selected placements.
    urls: string[];
  }) {
    const selections = Array.from(selected).map((id) => ({
      kind: "placement" as const,
      id,
    }));
    const ok = await share.generate(selections, opts);
    if (ok) {
      setSharePrepareOpen(false);
      clearSelection();
    }
  }

  // Delete the listing on the live portals (MyHome + SS) for one card's
  // publish group, then refresh so the card shows its greyed/deleted state.
  async function portalDelete(card: Placement): Promise<PlatformDeleteResult[]> {
    const res = await api<PlatformDeleteResult[]>(
      `/api/placements/${card.id}/portal-delete`,
      { method: "POST" },
    );
    await Promise.all([reload(), reloadStats()]);
    return res;
  }

  const filtersActive =
    !!userId || !!platform || !!debouncedSearch || selectedTypes.length > 0 ||
    !!dealType || selectedRooms.length > 0 || !!minPrice || !!maxPrice ||
    !!minArea || !!maxArea || !!floorMin || !!floorMax ||
    buildingStatuses.length > 0 || conditions.length > 0 || datePreset !== "all" ||
    selectedDistricts.length > 0 || selectedStreets.length > 0 || sort !== "published_desc" ||
    quick.myListings || quick.missingPhone || quick.missingOffer || quick.manualOnly || quick.thisWeek;

  const allOnPageSelected = (rows?.length ?? 0) > 0 && rows!.every((r) => selected.has(r.id));

  // Active-filter-group count for the Filters button badge. One per group;
  // search, platform and sort live in the toolbar so they're excluded.
  let filterCount = 0;
  if (userId) filterCount++;
  if (dealType) filterCount++;
  if (selectedTypes.length) filterCount++;
  if (selectedRooms.length) filterCount++;
  if (minPrice || maxPrice) filterCount++;
  if (minArea || maxArea) filterCount++;
  if (floorMin || floorMax) filterCount++;
  if (buildingStatuses.length) filterCount++;
  if (conditions.length) filterCount++;
  if (selectedDistricts.length) filterCount++;
  if (selectedStreets.length) filterCount++;
  if (datePreset !== "all") filterCount++;
  filterCount += Object.values(quick).filter(Boolean).length;

  // Removable chips mirroring the applied filters (see Fresh for the pattern).
  const chips: ActiveChip[] = [];
  if (debouncedSearch)
    chips.push({ key: "q", label: `“${debouncedSearch}”`, onRemove: () => setSearch("") });
  if (userId) {
    const m = members.find((x) => x.id === userId);
    chips.push({
      key: "user",
      label: m ? m.full_name || m.email : "Employee",
      onRemove: () => setUserId(""),
    });
  }
  (Object.keys(quick) as (keyof QuickFilter)[])
    .filter((k) => quick[k])
    .forEach((k) =>
      chips.push({ key: `quick:${k}`, label: QUICK_LABELS[k], onRemove: () => toggleQuick(k) }),
    );
  if (dealType)
    chips.push({
      key: "deal",
      label: DEAL_OPTIONS.find((o) => o.value === dealType)?.label ?? dealType,
      onRemove: () => setDealType(""),
    });
  selectedTypes.forEach((t) =>
    chips.push({
      key: `type:${t}`,
      label: PROPERTY_TYPE_LABEL[t] ?? t,
      onRemove: () => setSelectedTypes(selectedTypes.filter((x) => x !== t)),
    }),
  );
  selectedRooms.forEach((r) =>
    chips.push({
      key: `rooms:${r}`,
      label: `${r} rm`,
      onRemove: () => setSelectedRooms(selectedRooms.filter((x) => x !== r)),
    }),
  );
  if (minPrice || maxPrice)
    chips.push({
      key: "price",
      // Symbol-prefixed so the chip says WHICH currency the band is in —
      // "$100k–200k" and "₾100k–200k" are very different filters.
      label: rangeLabel(
        "",
        minPrice,
        maxPrice,
        (v) => `${priceCcy === "usd" ? "$" : "₾"}${compactNum(v)}`,
      ).trim(),
      onRemove: () => { setMinPrice(""); setMaxPrice(""); },
    });
  if (minArea || maxArea)
    chips.push({
      key: "area",
      label: rangeLabel("m²", minArea, maxArea, compactNum),
      onRemove: () => { setMinArea(""); setMaxArea(""); },
    });
  if (floorMin || floorMax)
    chips.push({
      key: "floor",
      label: rangeLabel("fl", floorMin, floorMax, (v) => v),
      onRemove: () => { setFloorMin(""); setFloorMax(""); },
    });
  buildingStatuses.forEach((b) =>
    chips.push({
      key: `bld:${b}`,
      label: BUILDING_FACETS.find((o) => o.value === b)?.label ?? b,
      onRemove: () => setBuildingStatuses(buildingStatuses.filter((x) => x !== b)),
    }),
  );
  conditions.forEach((c) =>
    chips.push({
      key: `cond:${c}`,
      label: CONDITION_FACETS.find((o) => o.value === c)?.label ?? c,
      onRemove: () => setConditions(conditions.filter((x) => x !== c)),
    }),
  );
  selectedDistricts.forEach((d) =>
    chips.push({
      key: `dist:${d}`,
      label: d,
      onRemove: () => setSelectedDistricts(selectedDistricts.filter((x) => x !== d)),
    }),
  );
  selectedStreets.forEach((st) =>
    chips.push({
      key: `street:${st}`,
      label: st,
      onRemove: () => setSelectedStreets(selectedStreets.filter((x) => x !== st)),
    }),
  );
  if (datePreset !== "all")
    chips.push({
      key: "date",
      label: DATE_OPTIONS.find((o) => o.value === datePreset)?.label ?? datePreset,
      onRemove: () => setDatePreset("all"),
    });

  const filterState: PlacementFilterState = {
    userId, setUserId, dealType, setDealType,
    selectedTypes, setSelectedTypes, selectedRooms, setSelectedRooms,
    minPrice, maxPrice, setMinPrice, setMaxPrice,
    priceCcy, setPriceCcy,
    minArea, maxArea, setMinArea, setMaxArea,
    floorMin, floorMax, setFloorMin, setFloorMax,
    buildingStatuses, setBuildingStatuses, conditions, setConditions,
    selectedDistricts, setSelectedDistricts, selectedStreets, setSelectedStreets,
    datePreset, setDatePreset, quick, toggleQuick,
  };

  return (
    <div className="w-full">
      <PageHeader
        title="Placements"
        subtitle={
          user?.role === "shareholder" || user?.role === "executive_manager" || user?.role === "operations_manager"
            ? "Every listing published across your agency."
            : user?.role === "lead_broker"
              ? "Listings published by you and your team."
              : "Your published listings."
        }
        actions={
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setShowManualModal(true)} className="btn-primary">
              + Add manual
            </button>
            <a href={buildCsvUrl()} className="btn-secondary" download>Export CSV</a>
          </div>
        }
      />

      {error && (
        <div className="card p-4 border-rose-500/25 bg-rose-500/[0.04] text-rose-300 text-sm mb-4">{error}</div>
      )}

      <StatsStrip stats={stats} quick={quick} toggle={toggleQuick} />

      {/* ── Toolbar (sticks under the top bar; the negative margins cancel
          Layout's px-4/6/8 so the bar spans the full content width). Search
          grows on the left, controls cluster right, every filter lives behind
          the single Filters button. ──── */}
      <div className="sticky top-14 z-20 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-2.5 mb-3 bg-ink-950/80 backdrop-blur-md border-b border-white/5 flex flex-wrap items-center gap-2">
        {/* Search — grows to fill available width */}
        <div className="relative flex-1 min-w-[10rem] order-1">
          <svg
            width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.3-4.3" />
          </svg>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Listing ID, district, owner, phone, title…"
            className="input !h-10 !py-0 !pl-9 !pr-8 !text-[13px] w-full"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 flex items-center justify-center rounded-md text-slate-500 hover:text-slate-200 hover:bg-white/5"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Filters button — prominent, with an active-group count badge */}
        <button
          type="button"
          onClick={() => setFiltersOpen(true)}
          className={`order-2 sm:order-3 inline-flex items-center gap-2 h-10 px-3.5 rounded-lg text-[13px] font-semibold transition ${
            filterCount > 0
              ? "bg-accent-500/15 text-accent-100 border border-accent-500/40"
              : "bg-ink-900/60 text-slate-200 border border-white/10 hover:border-white/20"
          }`}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 6h16M7 12h10M10 18h4" />
          </svg>
          Filters
          {filterCount > 0 && (
            <span className="inline-flex items-center justify-center min-w-[1.15rem] h-[1.15rem] px-1 rounded-full bg-accent-500 text-white text-[11px] font-bold tabular-nums">
              {filterCount}
            </span>
          )}
        </button>

        {/* Control cluster — platform, sort, page selection */}
        <div className="order-3 sm:order-2 sm:ml-auto flex items-center flex-wrap gap-2">
          <SegToggle
            options={PLATFORM_OPTIONS}
            value={platform}
            onChange={(v) => setPlatform(v as "" | Platform)}
          />
          <SingleSelectDropdown
            label="Sort"
            value={sort}
            options={SORT_OPTIONS}
            onChange={(v) => setSort(v as PlacementSort)}
            align="right"
          />
          {rows && rows.length > 0 && (
            <button
              type="button"
              onClick={allOnPageSelected ? clearSelection : selectAllOnPage}
              className="h-9 px-3 rounded-lg text-[12px] font-medium text-slate-300 border border-white/10 hover:border-white/20 hover:text-slate-100 transition whitespace-nowrap"
            >
              {allOnPageSelected ? "Clear selection" : "Select page"}
            </button>
          )}
          <ShareFromUrlsButton
            onClick={() => {
              share.setShareError(null);
              setUrlShareOpen(true);
            }}
          />
        </div>
      </div>

      {filtersOpen && (
        <PlacementFiltersPanel
          onClose={() => setFiltersOpen(false)}
          total={total}
          filterCount={filterCount}
          onClear={resetAllFilters}
          members={members}
          showEmployeeFilter={showEmployeeFilter}
          districts={districts}
          streets={streets}
          state={filterState}
        />
      )}

      {/* Active-filter chips — removable, with a clear-all */}
      <FilterChips chips={chips} onClearAll={resetAllFilters} />

      {share.shareError && (
        <div className="card p-3 mb-4 border-rose-500/25 bg-rose-500/[0.04] text-rose-300 text-sm">
          {share.shareError}
        </div>
      )}

      {share.shareLinks && (
        <ActivePublicLinksPanel
          links={share.shareLinks}
          onRevoke={share.revoke}
          onEdit={setEditLinkId}
        />
      )}
      {editLinkId && (
        <ShareEditModal
          linkId={editLinkId}
          onClose={() => setEditLinkId(null)}
          onChanged={share.loadShareLinks}
        />
      )}

      {/* Result count */}
      <div className="mb-3 text-xs text-slate-500 tabular-nums">
        {loadingPage && rows === null
          ? "Loading…"
          : total === 0
            ? "No placements match these filters."
            : `${total.toLocaleString()} placement${total === 1 ? "" : "s"}${filtersActive ? " (filtered)" : ""}`}
      </div>

      {/* Gallery grid */}
      {rows === null ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="card overflow-hidden border border-white/5">
              <Skeleton className="aspect-[16/11] w-full" />
              <div className="p-3 space-y-2">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
                <Skeleton className="h-3 w-2/3" />
              </div>
            </div>
          ))}
        </div>
      ) : rows.length === 0 ? (
        <div className="card p-10 text-center text-slate-500 text-sm border border-white/5">
          <div className="text-3xl mb-2">🏠</div>
          {filtersActive ? (
            <>
              <div>No placements match these filters.</div>
              <button type="button" onClick={resetAllFilters}
                className="mt-2 text-xs text-accent-400 hover:text-accent-300">Reset filters</button>
            </>
          ) : (
            <>No placements yet. Publish a listing from the Publish page or log one manually.</>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {rows.map((p) => (
            <div key={p.id} className="group/card h-full">
              <PlacementCard
                p={p}
                selected={selected.has(p.id)}
                selectable={true}
                canEditOffer={canEditOffer(p)}
                canEditPhone={canEditPhone(p)}
                canPortalDelete={canEditOffer(p)}
                onPortalDelete={() => portalDelete(p)}
                onToggleSelect={() => toggleOne(p.id)}
                onOpenPhoto={() => p.cover_photo_url && setLightbox({
                  src: p.cover_photo_url,
                  alt: p.external_id ? `Listing ${p.external_id}` : "",
                })}
                onUpdatePhone={(next) => setRows((rs) =>
                  rs ? rs.map((r) => r.id === p.id ? { ...r, owner_phone: next } : r) : rs
                )}
                onUpdateOffer={(next) => setRows((rs) =>
                  rs ? rs.map((r) => r.id === p.id ? { ...r, ...next } : r) : rs
                )}
                onUpdateNotes={(next) => setRows((rs) =>
                  rs ? rs.map((r) => r.id === p.id ? { ...r, notes: next } : r) : rs
                )}
              />
            </div>
          ))}
        </div>
      )}

      <Pagination
        page={page} totalPages={totalPages} pageSize={pageSize} total={total}
        windowStart={windowStart} windowEnd={windowEnd} loading={loadingPage}
        onPageChange={setPage} onPageSizeChange={setPageSize}
      />

      {selected.size > 0 && (
        <BulkBar
          count={selected.size}
          canDelete={canDelete}
          onSetOffer={bulkSetOffer}
          onClearOffer={bulkClearOffer}
          onDelete={bulkDelete}
          onShare={() => { share.setShareError(null); setSharePrepareOpen(true); }}
          onClear={clearSelection}
        />
      )}

      {sharePrepareOpen && (
        <SharePrepareModal
          count={selected.size}
          generating={share.generating}
          error={share.shareError}
          onGenerate={generateShare}
          onClose={() => setSharePrepareOpen(false)}
        />
      )}

      {/* Share from pasted URLs — no placement selection involved. */}
      {urlShareOpen && (
        <SharePrepareModal
          count={0}
          urlsOnly
          generating={share.generating}
          error={share.shareError}
          onGenerate={async (opts) => {
            const ok = await share.generate([], opts);
            if (ok) setUrlShareOpen(false);
          }}
          onClose={() => setUrlShareOpen(false)}
        />
      )}

      {share.shareModal && (
        <ShareLinkModal
          link={share.shareModal}
          onClose={() => share.setShareModal(null)}
        />
      )}

      {lightbox && (
        <Lightbox src={lightbox.src} alt={lightbox.alt} onClose={() => setLightbox(null)} />
      )}

      {showManualModal && (
        <ManualPlacementModal
          onClose={() => setShowManualModal(false)}
          onCreated={() => {
            setShowManualModal(false);
            // New row sorts to the top of "newest first" — jump to page 1
            // and refetch so the user sees it immediately.
            if (page !== 1) setPage(1);
            else { reload(); reloadStats(); }
          }}
        />
      )}
    </div>
  );
}
